import React, { useState, useEffect, useContext, useMemo } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, sp, rad, type, icBox, listRow } from "../../constants/styles";
import Ic from "../common/Ic";
import { formatPropAddr } from "../../utils/propertyAddr";
import useLocationFilter from "../../hooks/useLocationFilter";
import LocationBar from "../common/LocationBar";
import { withDistances, sortByDistance, filterByRadius, formatDistance } from "../../utils/geo";

// Subtype lists, mirrored from PropFormPage. Used to (a) decide
// whether the type chip should render in residential or commercial
// colour, and (b) defensively map the legacy literal "commercial"
// returned by older rows.
const RES_TYPES_CLIENT = ['apartment', 'independent_house', 'villa', 'pg_room'];
const COM_TYPES_CLIENT = ['office', 'shop', 'showroom', 'warehouse', 'clinic', 'coworking', 'industrial', 'commercial'];

export default function AdminProperties() {
  const { h, go, setSelOrgId, showConfirm, flash } = useContext(Ctx);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  // Orthogonal occupancy filter so admin can pull "vacant commercial"
  // / "occupied residential" without re-running queries by hand.
  const [occupancy, setOccupancy] = useState("all");

  // Geocode coverage — how many properties have lat/lng vs not. Drives
  // the backfill card visibility (only shown when there's work to do)
  // and the "Backfill missing" button's progress states.
  const [geoStatus, setGeoStatus] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  // Location filter (shared hook) — lets the admin sort + radius-filter
  // the platform-wide property list by distance from a chosen point.
  // Useful for support cases like "show me all properties within 5 km
  // of Madurai" or "list everything near this org's HQ".
  const locFilter = useLocationFilter({ storageKey: 'adminPropertiesLocation' });

  // Apply distance enrichment + radius filter + nearest-first sort when
  // the admin has set a location. Properties without coords get
  // Infinity and naturally sink to the bottom; with a radius active,
  // they're excluded. Without a location, returns the raw items.
  const displayItems = useMemo(() => {
    if (!locFilter.userLocation) return items;
    const enriched = withDistances(items, locFilter.userLocation);
    const filtered = filterByRadius(enriched, locFilter.radiusKm);
    return sortByDistance(filtered);
  }, [items, locFilter.userLocation, locFilter.radiusKm]);

  const loadGeoStatus = async () => {
    const res = await api.adminGetGeocodeStatus();
    if (res.success) setGeoStatus(res);
  };

  useEffect(() => { loadGeoStatus(); }, []);

  const runBackfill = () => {
    const n = geoStatus?.without_coords || 0;
    const seconds = Math.ceil(n * 1.1); // ~1.1s per row at Nominatim's free tier
    const msg = `Geocode ${n} ${n === 1 ? 'property' : 'properties'} missing coordinates? This may take ~${seconds}s (1 query/sec to OpenStreetMap Nominatim).`;
    showConfirm(msg, async () => {
      setBackfilling(true);
      setBackfillResult(null);
      const res = await api.adminGeocodeBackfill();
      setBackfilling(false);
      if (res.success) {
        setBackfillResult({ updated: res.updated, failed: res.failed, processed: res.processed });
        flash?.(`✅ Geocoded ${res.updated}/${res.processed} properties`, "success");
        await loadGeoStatus();
      } else {
        flash?.(res.message || "Backfill failed", "error");
      }
    });
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filter !== "all") params.type = filter;
      if (occupancy !== "all") params.occupancy = occupancy;
      const res = await api.adminGetProperties(params);
      if (res.success) setItems(res.properties || []);
      setLoading(false);
    };
    const timer = setTimeout(load, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [search, filter, occupancy]);

  const filters = ["all", "residential", "commercial"];
  const occupancyFilters = ["all", "occupied", "vacant"];
  const tabS = (active) => ({
    padding: `${sp.xs}px ${sp.md}px`, borderRadius: rad.pill, border: "none",
    background: active ? h.ac : "transparent", color: active ? "#fff" : h.ts,
    fontSize: 11, fontWeight: active ? 600 : 400, cursor: "pointer",
  });

  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
      <Ic n="arrow-left" s={14} c={h.ac} /> Dashboard
    </button>

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.lg }}>
      <h2 style={{ ...type.h2, color: h.tx, margin: 0 }}>All Properties</h2>
      <span style={{ ...type.xs, color: h.ts }}>{items.length} found</span>
    </div>

    {/* Geocode coverage + backfill — only surfaces when there's work
        to do. Properties without lat/lng don't appear in the public
        marketplace's distance-sorted list, so getting them geocoded
        improves discovery for those listings. The actual call is
        rate-limited (1/sec) by Nominatim, so the button stays in
        "Working…" state until done — typically <5 min even for
        hundreds of properties. Best to run during low-traffic. */}
    {geoStatus && geoStatus.without_coords > 0 && (
      <div style={{
        ...crd(h),
        marginBottom: sp.md,
        borderLeft: `4px solid ${h.wn}`,
        background: `${h.wn}06`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.sm }}>
          <div style={icBox(h.wn, 36)}>
            <span style={{ fontSize: 16 }}>{"📍"}</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>
              {geoStatus.without_coords} of {geoStatus.total} properties missing coordinates
            </div>
            <div style={{ ...type.xs, color: h.sc, marginTop: 2, lineHeight: 1.4 }}>
              These won't show up in the public marketplace's "near me" sorted view. Tap to geocode them from their address (OpenStreetMap, ~1s each).
            </div>
          </div>
        </div>
        <button
          onClick={runBackfill}
          disabled={backfilling}
          style={{
            width: "100%",
            padding: `${sp.sm + 2}px ${sp.lg}px`,
            borderRadius: rad.sm,
            border: `1px solid ${h.wn}40`,
            background: backfilling ? `${h.wn}10` : `${h.wn}08`,
            color: h.wn,
            ...type.xs,
            fontWeight: 700,
            cursor: backfilling ? "wait" : "pointer",
            opacity: backfilling ? 0.7 : 1,
          }}>
          {backfilling
            ? `Geocoding… (${geoStatus.without_coords} to process)`
            : `🗺️ Backfill ${geoStatus.without_coords} missing ${geoStatus.without_coords === 1 ? 'coordinate' : 'coordinates'}`}
        </button>
        {backfillResult && (
          <div style={{
            marginTop: sp.sm,
            padding: `${sp.xs + 2}px ${sp.sm}px`,
            borderRadius: rad.sm,
            background: `${h.gn}10`,
            border: `1px solid ${h.gn}25`,
            ...type.xs, color: h.gn, fontWeight: 600,
          }}>
            ✅ {backfillResult.updated} updated · {backfillResult.failed} no match
          </div>
        )}
      </div>
    )}
    {geoStatus && geoStatus.without_coords === 0 && geoStatus.total > 0 && (
      <div style={{
        marginBottom: sp.md,
        padding: `${sp.sm}px ${sp.md}px`,
        borderRadius: rad.sm,
        background: `${h.gn}08`,
        border: `1px solid ${h.gn}20`,
        ...type.xs, color: h.gn, fontWeight: 600,
        display: "flex", alignItems: "center", gap: sp.xs,
      }}>
        <span>{"✅"}</span> All {geoStatus.total} properties have coordinates
      </div>
    )}

    {/* Search */}
    <div style={{ position: "relative", marginBottom: sp.md }}>
      <Ic n="search" s={16} c={h.tt} />
      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search name or address..."
        style={{ width: "100%", padding: `${sp.sm}px ${sp.md}px`, paddingLeft: 36, borderRadius: rad.md, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, ...type.sm, outline: "none", boxSizing: "border-box" }}
      />
    </div>

    {/* Filters — type (residential / commercial) on row 1, occupancy
        (occupied / vacant) on row 2 so they read as independent dimensions
        rather than a single 5-button strip. */}
    <div style={{ display: "flex", gap: sp.xs, marginBottom: sp.sm, flexWrap: "wrap" }}>
      {filters.map(f => <button key={f} onClick={() => setFilter(f)} style={tabS(filter === f)}>
        {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
      </button>)}
    </div>
    <div style={{ display: "flex", gap: sp.xs, marginBottom: sp.lg, flexWrap: "wrap" }}>
      {occupancyFilters.map(f => <button key={f} onClick={() => setOccupancy(f)} style={tabS(occupancy === f)}>
        {f === "all" ? "Any status" : f.charAt(0).toUpperCase() + f.slice(1)}
      </button>)}
    </div>

    {/* Location bar — admin can sort + radius-filter the platform-wide
        property list by distance from a chosen point. Same shared
        hook + component used by PublicBrowsePage and the tenant
        MapViewPage; storage key is namespaced so the admin's active
        reference doesn't leak into either marketplace surface. */}
    <LocationBar filter={locFilter} h={h} t={{}} />

    {loading ? (
      <div style={{ textAlign: "center", padding: sp.xxxl }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
      </div>
    ) : displayItems.length === 0 ? (
      <div style={{ ...crd(h), textAlign: "center", padding: sp.xxxl }}>
        <Ic n="home" s={28} c={h.tt} />
        <div style={{ ...type.sm, color: h.tt, marginTop: sp.sm }}>
          {locFilter.userLocation && locFilter.radiusKm
            ? `No properties within ${locFilter.radiusKm} km of ${locFilter.locationLabel || 'your location'}`
            : 'No properties found'}
        </div>
      </div>
    ) : (
      <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
        {displayItems.map((p, i) => {
          // Combined door-no + street address — same formatter the
          // tenant + landlord views use, so admin sees the same string.
          const fullAddr = formatPropAddr({ property_label: p.name, addr: p.address });
          // Whole row deep-links to the org's admin detail since there
          // is no admin-property-detail page; the parent org is the
          // closest "see all data behind this property" surface.
          const goToOrg = () => { if (p.org_id) { setSelOrgId(p.org_id); go("adminOrgDetail"); } };
          const isCom = COM_TYPES_CLIENT.includes(p.type);
          return (
            <div
              key={p.id}
              onClick={goToOrg}
              style={{ ...listRow(h, i === displayItems.length - 1), cursor: p.org_id ? "pointer" : "default" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: sp.md, flex: 1, minWidth: 0 }}>
                <div style={icBox(h.gn, 36)}>
                  <Ic n="home" s={16} c={h.gn} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Line 1: org name + landlord name */}
                  <div style={{ ...type.sm, color: h.tx, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.org_name || "—"}
                    {p.landlord_name && <span style={{ color: h.ts, fontWeight: 400 }}> {"·"} {p.landlord_name}</span>}
                  </div>
                  {/* Line 2: full address (door / flat no + street) */}
                  <div style={{ ...type.xs, color: h.tt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 }}>
                    {fullAddr || "No address"}
                  </div>
                  {/* Line 3: occupancy + tenant name + (optional) distance */}
                  <div style={{ display: "flex", alignItems: "center", gap: sp.xs, marginTop: 4, flexWrap: "wrap" }}>
                    <span style={bdg(
                      p.occupied ? `${h.gn}18` : `${h.wn}18`,
                      p.occupied ? h.gn : h.wn
                    )}>{p.occupied ? "Occupied" : "Vacant"}</span>
                    {p.occupied && p.tenant_name && <span style={{ ...type.xs, color: h.ts }}>{p.tenant_name}</span>}
                    {/* Distance pill — only when admin has set a reference
                        location AND this row has coords. Properties without
                        coords show no pill (rather than misleading "0 m"). */}
                    {p._distance_km != null && Number.isFinite(p._distance_km) && (
                      <span style={{ ...bdg(`${h.ac}15`, h.ac), display: "inline-flex", alignItems: "center", gap: 3 }}>
                        <span style={{ fontSize: 9 }}>{"📍"}</span>
                        {formatDistance(p._distance_km)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0, marginLeft: sp.sm }}>
                <span style={bdg(isCom ? `${h.pp}20` : `${h.ac}20`, isCom ? h.pp : h.ac)}>
                  {p.type || "residential"}
                </span>
                {p.rent && <div style={{ ...type.xs, color: h.gn, marginTop: sp.xs }}>{"₹"}{p.rent}</div>}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>;
}
